import React from 'react';
import PropTypes from 'prop-types';
import debounce from 'js/timing-utils';
import imageResizer from 'js/image-resizer';
import cn from 'classnames';
import { createHtmlImage } from 'js/dom-image-utils';

const FirstLoadSizeMultiplier = 0.05; // First time we preload smaller image in order to reduce wait time.

/**
 * Image
 *
 * Key notes:
 * - automatically responsive by default (size is automatically calculated from container size)
 * - default transform is 'fill'
 * - width or height can be fixed (useful for fluid images), in this case default transform is 'fit'
 * - when both width and height are defined it becomes unresponsive.
 * - can be rendered as fluid or ordinary image
 * - resolution it is kind of steps for automatic size calculation, for example if resolution is 100, horizontal image width could be 100,200,300...1200... and height is calculated according to aspect ratio and vise versa for vertical image
 * - default resolutions for images with size > 100 is 100 for < 100 is 10
 * - device pixel ratio is taken into account for calculating image size
 *
 * Transforms:
 * 'fill' - image is cropped according to container proportions to fill it completely.
 * 'fit' - image is resized with saving original proportions to fit container by width or height.
 */
class Image extends React.Component {
  static transforms = {
    fill: 'fill',
    fit: 'fit',
  };

  static propTypes = {
    className: PropTypes.string,
    src: PropTypes.string.isRequired,
    alt: PropTypes.string,
    transform: PropTypes.oneOf(Object.values(Image.transforms)),
    fluid: PropTypes.bool,
    focusPoint: PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
    }),
    width: PropTypes.number,
    height: PropTypes.number,
    resolution: PropTypes.number,
  };

  static viewModelMeta = {
    className: 'ignore',
    transform: 'ignore',
    fluid: 'ignore',
    width: 'ignore',
    height: 'ignore',
    resolution: 'ignore',
  };

  canBeResized = imageResizer.checkImageCanBeResized(this.props.src);
  autoTransform = this.constructor.transforms.fill;
  autoCalcWidthOnly = false;
  autoCalcHeightOnly = false;

  defaultSrc = !this.canBeResized
    ? this.props.src
    : this.props.width > 0 && this.props.height > 0
      ? imageResizer.buildResizeUrl(
          this.props.src,
          this.props.width,
          this.props.height,
          this.props.transform ||
            this.autoTransform === this.constructor.transforms.fill
            ? imageResizer.Transforms.downFill
            : imageResizer.Transforms.downFit
        )
      : undefined;

  state = {
    src: this.defaultSrc,
    width: 0,
    height: 0,
    ratio: 0,
    containerOffsetWidth: 0,
    containerOffsetHeight: 0,
  };

  preloadImage = createHtmlImage();
  firstPreloadImage = createHtmlImage();

  handleResize = () => {
    if (
      !this.container ||
      (!this.container.offsetWidth && !this.container.offsetHeight)
    ) {
      return;
    }

    if (!this.container.offsetWidth || !this.container.offsetHeight) {
      this.autoTransform = Image.transforms.fit;
      this.autoCalcWidthOnly = this.container.offsetWidth > 0;
      this.autoCalcHeightOnly = this.container.offsetHeight > 0;
    }

    const skipByWidth = this.props.width
      ? this.state.width === this.props.width
      : this.container.offsetWidth === this.state.containerOffsetWidth;

    const skipByHeight = this.props.height
      ? this.state.height === this.props.height
      : this.container.offsetHeight === this.state.containerOffsetHeight;

    if (
      skipByWidth &&
      skipByHeight &&
      (this.firstPreloadImage.src || this.state.src)
    ) {
      return;
    }

    const resolutionByWidth =
      !this.props.width &&
      this.container.offsetWidth > this.container.offsetHeight;

    let resolution = this.props.resolution;
    if (!resolution) {
      const resolutionSize = resolutionByWidth
        ? this.container.offsetWidth
        : this.container.offsetHeight;
      resolution = resolutionSize > 100 ? 100 : 10;
    }

    const widthResolution = resolutionByWidth ? resolution : 1;
    const heightResolution = resolutionByWidth ? 1 : resolution;

    let newWidth =
      this.props.width ??
      (this.autoCalcHeightOnly
        ? 0
        : imageResizer.calcImageWidth(
            this.container.offsetWidth,
            widthResolution
          ));
    let newHeight =
      this.props.height ??
      (this.autoCalcWidthOnly
        ? 0
        : imageResizer.calcImageHeight(
            this.container.offsetHeight,
            heightResolution
          ));

    if (resolutionByWidth && !this.autoCalcWidthOnly && !this.props.height) {
      newHeight = Math.ceil(
        newWidth / (this.container.offsetWidth / this.container.offsetHeight)
      );
    } else if (
      !resolutionByWidth &&
      !this.autoCalcHeightOnly &&
      !this.props.width
    ) {
      newWidth = Math.ceil(
        newHeight / (this.container.offsetHeight / this.container.offsetWidth)
      );
    }

    const newRatio = newHeight > 0 ? newWidth / newHeight : 1;

    this.setState(prevState => {
      const currentWidth = prevState.width ?? 0;
      const currentHeight = prevState.height ?? 0;
      const currentRatio = prevState.ratio ?? 1;

      if (
        currentWidth >= newWidth &&
        currentHeight >= newHeight &&
        currentRatio === newRatio
      ) {
        return {
          containerOffsetWidth: this.container?.offsetWidth ?? 0,
          containerOffsetHeight: this.container?.offsetHeight ?? 0,
          width: currentWidth,
          height: currentHeight,
          ratio: currentRatio,
        };
      }

      const newSrc = imageResizer.buildResizeUrl(
        this.props.src,
        newWidth,
        newHeight,
        (this.props.transform || this.autoTransform) === Image.transforms.fill
          ? imageResizer.Transforms.downFill
          : imageResizer.Transforms.downFit,
        this.props.focusPoint?.x ?? 0,
        this.props.focusPoint?.y ?? 0
      );

      if (!this.firstPreloadImage.src && !this.state.src) {
        const firstLoadWidth = Math.ceil(newWidth * FirstLoadSizeMultiplier);
        const firstLoadHeight = Math.ceil(newHeight * FirstLoadSizeMultiplier);
        if (firstLoadWidth > 0 && firstLoadHeight > 0) {
          this.firstPreloadImage.src = imageResizer.buildResizeUrl(
            this.props.src,
            firstLoadWidth,
            firstLoadHeight,
            (this.props.transform || this.autoTransform) ===
              Image.transforms.fill
              ? imageResizer.Transforms.downFill
              : imageResizer.Transforms.downFit,
            this.props.focusPoint ? this.props.focusPoint.x : null,
            this.props.focusPoint ? this.props.focusPoint.y : null
          );
        }
      }

      if (newSrc !== this.state.src) {
        this.preloadImage.src = newSrc;
      }

      return {
        width: newWidth,
        height: newHeight,
        ratio: newRatio,
        containerOffsetWidth: this.container?.offsetWidth ?? 0,
        containerOffsetHeight: this.container?.offsetHeight ?? 0,
      };
    });
  };

  debouncedHandleResize = debounce(this.handleResize, 300);

  onFirstSrcPreloaded = () => {
    this.setState(prevState => {
      return { src: prevState.src ?? this.firstPreloadImage.src };
    });
  };

  onSrcPreloaded = () => {
    this.setState({ src: this.preloadImage.src });
  };

  checkElementIsHidden(element) {
    return !element.offsetParent;
  }

  isHidden() {
    return this.checkElementIsHidden(this.container);
  }

  getHiddenRoot() {
    if (!this.isHidden()) {
      return null;
    }

    var parent = this.container?.parentElement ?? null;
    while (parent && this.checkElementIsHidden(parent)) {
      parent = parent.parentElement;
    }

    return parent;
  }

  onObservedChange = () => {
    if (!this.isHidden()) {
      this.debouncedHandleResize();
    }
  };

  componentDidMount() {
    this.firstPreloadImage.onload = this.onFirstSrcPreloaded;
    this.preloadImage.onload = this.onSrcPreloaded;
    if (this.canBeResized) {
      this.handleResize();
      this.debouncedHandleResize();
      window.addEventListener('resize', this.debouncedHandleResize);
    }

    if (this.canBeResized) {
      this.observer = new MutationObserver(this.onObservedChange);
      if (this.isHidden() && this.observer) {
        const root = this.getHiddenRoot();
        if (root) {
          this.observer.observe(root, { attributes: true });
        }
      }
    }
  }

  componentWillUnmount() {
    if (this.canBeResized) {
      window.removeEventListener('resize', this.debouncedHandleResize);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  componentDidUpdate() {
    if (this.canBeResized) this.debouncedHandleResize();
  }

  render() {
    return (
      <React.Fragment>
        {!this.props.fluid ? (
          <span
            className={cn('image', this.props.className)}
            ref={c => (this.container = c)}
          >
            {this.state.src && (
              <img
                className="image__img"
                src={this.state.src}
                alt={this.props.alt || ''}
              />
            )}
          </span>
        ) : (
          <span
            className={cn('image image_fluid', this.props.className)}
            style={{
              backgroundImage: this.state.src
                ? `url(${this.state.src})`
                : undefined,
              backgroundPosition: this.props.focusPoint
                ? `${this.props.focusPoint.x}% ${this.props.focusPoint.y}%`
                : undefined,
            }}
            ref={c => {
              this.container = c;
            }}
          >
            {this.state.src && (
              <img
                className="image__img"
                src={this.state.src}
                alt={this.props.alt || ''}
              />
            )}
          </span>
        )}
      </React.Fragment>
    );
  }
}

export default Image;
